import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../ui/Icon';
import { COURIERS, CourierKey } from '../ui/couriers';
import {
  MAP_W, MAP_H, GeoPlace, Projection, fitProjection, shapeToPath, useEuropeShapes, findCity,
} from './europeGeo';

/* Mapa dostaw 2.0 — żywe śledzenie pojedynczych paczek.
   Każda spakowana paczka podróżuje po trasie swojego kuriera z huba do miasta
   docelowego; jej pozycja na łuku odzwierciedla aktualny stan (nadana →
   w drodze → w doręczeniu → dostarczona). Paczki w kompletowaniu krążą wokół
   huba. Animacja sterowana zegarem klatek (requestAnimationFrame), dzięki czemu
   mapa jest „żywa” i pokazuje ruch przesyłek w czasie rzeczywistym.
   Kontury krajów i pozycje miast: prawdziwe dane kartograficzne (europeGeo). */

export type Delivery = {
  city: string;
  count: number;
  courier: CourierKey;
  live: boolean;
  lat: number;
  lon: number;
};

export type Hub = GeoPlace; // { label, lat, lon } — z danych firmy (nadawcy) tenanta

export const DEFAULT_HUB: Hub = findCity('Kraków')!;

// Statusy zdarzeń trackingu UCS (GET /v1/track/{trackingNumber}).
export type TrackStatus = 'pending' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'exception' | 'returned';

// Pojedyncza śledzona paczka. `status` jest faktem z danych (kompletowanie vs
// wysłana). Gdy paczka ma realny tracking UCS, pola `live*` niosą ostatnie
// zdarzenie kuriera — wtedy mapa steruje fazą i pozycją znacznika z danych
// rzeczywistych. Bez trackingu etap wyliczany jest poglądowo z zegara/odległości.
export type ParcelStatus = 'preparing' | 'shipped';
export type TrackedParcel = {
  id: string;
  orderName: string;
  customer?: string;
  city: string;
  zip?: string;      // kod pocztowy odbiorcy (do panelu „do kogo")
  country?: string;  // kraj docelowy, gdy inny niż PL
  courier: CourierKey;
  lat: number;
  lon: number;
  status: ParcelStatus;
  distanceKm: number;
  seed: number;      // 0..1 — deterministyczne przesunięcie fazy (paczki nie startują równo)
  tracking: string;  // numer śledzenia (realny UCS) lub wewnętrzna referencja
  trackingNumber?: string; // realny numer UCS (gdy jest — tracking na żywo)
  // Ostatnie zdarzenie z UCS (gdy dostępne):
  liveStatus?: TrackStatus;
  liveDesc?: string;
  liveCity?: string;
  liveLat?: number;
  liveLon?: number;
  liveAt?: string;
};

type ParcelPhase = 'preparing' | 'dispatched' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'exception' | 'returned';

const PHASE_LABEL: Record<ParcelPhase, string> = {
  preparing: 'Kompletowanie',
  dispatched: 'Nadana',
  in_transit: 'W drodze',
  out_for_delivery: 'W doręczeniu',
  delivered: 'Dostarczona',
  exception: 'Wyjątek',
  returned: 'Zwrot',
};

// Status UCS -> faza prezentacji na mapie.
const STATUS_TO_PHASE: Record<TrackStatus, ParcelPhase> = {
  pending: 'dispatched',
  in_transit: 'in_transit',
  out_for_delivery: 'out_for_delivery',
  delivered: 'delivered',
  exception: 'exception',
  returned: 'returned',
};

// Przybliżony postęp na trasie dla statusu UCS (gdy brak współrzędnych zdarzenia).
const PHASE_PROGRESS: Record<ParcelPhase, number> = {
  preparing: 0,
  dispatched: 0.06,
  in_transit: 0.5,
  out_for_delivery: 0.85,
  delivered: 1,
  exception: 0.5,
  returned: 0.3,
};

function courierColor(c: CourierKey) {
  return COURIERS[c]?.color || 'var(--fg-3)';
}

/* ---- geometria łuku trasy (kwadratowa krzywa Béziera) ---- */
function controlPoint(from: [number, number], to: [number, number]): [number, number] {
  const [x1, y1] = from; const [x2, y2] = to;
  const mx = (x1 + x2) / 2; const my = (y1 + y2) / 2;
  const dx = x2 - x1; const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const off = Math.min(120, len * 0.22);
  return [mx - (dy / len) * off, my + (dx / len) * off];
}

function arcPath(from: [number, number], to: [number, number]) {
  const c = controlPoint(from, to);
  return `M${from[0]},${from[1]} Q${c[0]},${c[1]} ${to[0]},${to[1]}`;
}

// Punkt na krzywej kwadratowej dla parametru t∈[0,1].
function quadAt(p0: [number, number], c: [number, number], p2: [number, number], t: number): [number, number] {
  const u = 1 - t;
  return [
    u * u * p0[0] + 2 * u * t * c[0] + t * t * p2[0],
    u * u * p0[1] + 2 * u * t * c[1] + t * t * p2[1],
  ];
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// Czas „przejazdu” paczki (ms) skalowany odległością — wizualny, nie zegarowy.
function travelMs(distanceKm: number) {
  return clamp(11000 + distanceKm * 7, 13000, 30000);
}
const REST_MS = 3800; // pauza „dostarczona” na końcu cyklu, zanim ruszy kolejna przesyłka

// Pozycja paczki w kompletowaniu — powolne krążenie wokół huba.
function hubOrbit(hubXY: [number, number], p: TrackedParcel, now: number): [number, number] {
  const a = p.seed * Math.PI * 2 + now * 0.00012;
  const rr = 28 + Math.sin(now * 0.0022 + p.seed * 7) * 3.2;
  return [hubXY[0] + Math.cos(a) * rr, hubXY[1] + Math.sin(a) * rr * 0.62];
}

type Resolved = { phase: ParcelPhase; progress: number; pt: [number, number]; real: boolean };

/* Rozwiązuje etap, postęp i pozycję paczki. Priorytet: realne zdarzenie UCS
   (faza + geokodowana lokalizacja). Bez trackingu — animacja poglądowa z zegara:
   paczka „shipped” podróżuje trasą, „preparing” krąży wokół huba. */
function resolveParcel(
  p: TrackedParcel, now: number, hubXY: [number, number], ctrl: [number, number], dest: [number, number],
  proj: Projection,
): Resolved {
  // 1) Realny tracking UCS.
  if (p.liveStatus) {
    const phase = STATUS_TO_PHASE[p.liveStatus];
    const bob = Math.sin(now * 0.002 + p.seed * 6) * 1.4;
    if (p.liveLat != null && p.liveLon != null) {
      const [px, py] = proj.project(p.liveLon, p.liveLat);
      const dHub = Math.hypot(px - hubXY[0], py - hubXY[1]);
      const dDest = Math.hypot(dest[0] - hubXY[0], dest[1] - hubXY[1]) || 1;
      return { phase, progress: clamp(dHub / dDest, 0.04, 1), pt: [px, py + bob], real: true };
    }
    const progress = PHASE_PROGRESS[phase];
    const pt = progress >= 1 ? dest : quadAt(hubXY, ctrl, dest, progress);
    return { phase, progress, pt: [pt[0], pt[1] + bob], real: true };
  }
  // 2) Kompletowanie — krąży wokół huba.
  if (p.status === 'preparing') return { phase: 'preparing', progress: 0, pt: hubOrbit(hubXY, p, now), real: false };
  // 3) Animacja poglądowa wzdłuż trasy.
  const tMs = travelMs(p.distanceKm);
  const cycle = tMs + REST_MS;
  const e = (((now + p.seed * cycle) % cycle) + cycle) % cycle;
  if (e >= tMs) return { phase: 'delivered', progress: 1, pt: dest, real: false };
  const progress = e / tMs;
  const phase: ParcelPhase = progress < 0.06 ? 'dispatched' : progress > 0.82 ? 'out_for_delivery' : 'in_transit';
  return { phase, progress, pt: quadAt(hubXY, ctrl, dest, progress), real: false };
}

function etaText(p: TrackedParcel, phase: ParcelPhase, progress: number): string {
  if (phase === 'preparing') return 'Kompletowanie na hubie';
  if (phase === 'delivered') return 'Dostarczona';
  if (phase === 'exception') return 'Wyjątek w doręczeniu';
  if (phase === 'returned') return 'Przesyłka zwracana';
  const totalH = Math.max(4, Math.round(p.distanceKm / 40));
  const remH = Math.max(1, Math.ceil((1 - progress) * totalH));
  return `ETA ~${remH} h`;
}

// Czytelny czas ostatniego zdarzenia („3 min” / „2 h” / „wczoraj”).
function agoText(iso?: string): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const min = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (min < 1) return 'przed chwilą';
  if (min < 60) return `${min} min temu`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h temu`;
  return `${Math.floor(h / 24)} dni temu`;
}

/* Klucz odbiorcy — grupuje paczki tej samej osoby w jednym mieście (nazwany
   klient) lub trzyma paczkę osobno, gdy nie znamy klienta. Dzięki temu po
   kliknięciu paczki pokazujemy „do kogo i ile paczek" idzie do tego odbiorcy. */
function recipientKey(p: TrackedParcel): string {
  return p.customer
    ? `c:${p.customer.toLowerCase().trim()}|${p.city.toLowerCase()}`
    : `o:${p.orderName}`;
}

// Polska odmiana rzeczownika „paczka" przez liczbę.
function plPackages(n: number): string {
  const m10 = n % 10; const m100 = n % 100;
  if (n === 1) return 'paczka';
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'paczki';
  return 'paczek';
}

/* Stabilny status do panelu (nie zależy od zegara animacji): realne zdarzenie
   UCS > kompletowanie > podgląd „w drodze". */
function displayStatus(p: TrackedParcel): { label: string; phase: ParcelPhase; real: boolean } {
  if (p.liveStatus) { const phase = STATUS_TO_PHASE[p.liveStatus]; return { label: PHASE_LABEL[phase], phase, real: true }; }
  if (p.status === 'preparing') return { label: PHASE_LABEL.preparing, phase: 'preparing', real: false };
  return { label: PHASE_LABEL.in_transit, phase: 'in_transit', real: false };
}

// Faza -> klasa kropki statusu (te same kolory co legenda etapów na mapie).
const PHASE_DOT: Record<ParcelPhase, string> = {
  preparing: 'prep', dispatched: 'transit', in_transit: 'transit',
  out_for_delivery: 'ofd', delivered: 'done', exception: 'ofd', returned: 'ofd',
};

type ParcelHover = { p: TrackedParcel; phase: ParcelPhase; progress: number; real: boolean; x: number; y: number };

// Limit liczby JEDNOCZEŚNIE animowanych znaczników paczek (wydajność pętli rAF).
// Liczniki w nagłówku (przesyłek / w drodze / kompletowanie) liczą z PEŁNEGO zbioru —
// ten limit dotyczy wyłącznie renderu ruchomych znaczników, nie danych.
const MAX_ANIMATED_PARCELS = 60;

/* Warstwa animowanych paczek — własny zegar klatek, odizolowana od reszty SVG,
   więc kontury krajów i znaczniki miast nie przeliczają się co klatkę. */
function ParcelLayer({ parcels, hubXY, proj, onHover, onSelect }: {
  parcels: TrackedParcel[];
  hubXY: [number, number];
  proj: Projection;
  onHover: (h: ParcelHover | null) => void;
  onSelect: (p: TrackedParcel) => void;
}) {
  const [now, setNow] = useState(() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
  useEffect(() => {
    let raf = 0;
    let last = 0;
    const loop = (t: number) => {
      if (t - last > 45) { setNow(t); last = t; } // ~22 fps — płynnie i oszczędnie
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <g className="parcel-layer">
      {parcels.slice(0, MAX_ANIMATED_PARCELS).map((p) => {
        const dest = proj.project(p.lon, p.lat);
        const ctrl = controlPoint(hubXY, dest);
        const { phase, progress, pt, real } = resolveParcel(p, now, hubXY, ctrl, dest, proj);
        const col = courierColor(p.courier);
        return (
          <g key={p.id}>
            {phase !== 'preparing' && progress > 0.01 && (
              <path
                className="parcel-trail"
                d={`M${hubXY[0]},${hubXY[1]} Q${ctrl[0]},${ctrl[1]} ${dest[0]},${dest[1]}`}
                stroke={col}
                pathLength={1}
                strokeDasharray={`${progress} 1`}
              />
            )}
            <g
              className={`parcel ${phase}${real ? ' real' : ''}`}
              transform={`translate(${pt[0].toFixed(1)},${pt[1].toFixed(1)})`}
              onMouseEnter={() => onHover({ p, phase, progress, real, x: pt[0], y: pt[1] })}
              onMouseLeave={() => onHover(null)}
              /* Wybór odbiorcy na pointerdown, nie onClick: znacznik paczki nieustannie
                 animuje (rAF), więc między mousedown a mouseup dryfuje spod kursora —
                 przeglądarka generuje wtedy 'click' na tle SVG (nie na paczce), a tło
                 czyściło zaznaczenie. Pointerdown łapie press od razu (działa też dla
                 dotyku/ściany); stopPropagation blokuje czyszczenie tła SVG. */
              onPointerDown={(e) => { e.stopPropagation(); onSelect(p); }}
            >
              <circle className="pglow" r="9" fill={col} filter="url(#pf-glow)" />
              {phase === 'out_for_delivery' && <circle className="pring" r="10" stroke={col} fill="none" />}
              {phase === 'delivered' && <circle className="pring done" r="10" stroke="var(--success-text)" fill="none" />}
              {(phase === 'exception' || phase === 'returned') && (
                <circle className="pring alert" r="10" stroke={phase === 'exception' ? 'var(--danger-text)' : 'var(--warning-text)'} fill="none" />
              )}
              <g className="pkg-wrap">
                <rect className="pkg" x="-5" y="-5" width="10" height="10" rx="2.5" fill={col} />
                <line x1="0" y1="-5" x2="0" y2="5" stroke="var(--surface)" strokeWidth="1" opacity="0.7" />
                <line x1="-5" y1="0" x2="5" y2="0" stroke="var(--surface)" strokeWidth="0.8" opacity="0.5" />
              </g>
              {phase === 'delivered' && (
                <path className="pcheck" d="M-2.6,0 L-0.7,1.9 L2.7,-2.1" fill="none" />
              )}
              {real && <circle className="plive" cx="6.5" cy="-6.5" r="2.2" />}
              <circle className="parcel-hit" r="15" fill="transparent" />
              <title>{`${p.orderName} · ${COURIERS[p.courier].label}\n${PHASE_LABEL[phase]} → ${real && p.liveCity ? p.liveCity : p.city}${real ? ' · UCS na żywo' : ''}\n${p.tracking}`}</title>
            </g>
          </g>
        );
      })}
    </g>
  );
}

type Props = {
  deliveries: Delivery[];
  parcels?: TrackedParcel[];
  packedToday?: number;
  hub?: Hub;
  wall?: boolean;
};

export default function DeliveryMap({ deliveries, parcels = [], packedToday, hub = DEFAULT_HUB, wall }: Props) {
  const shapes = useEuropeShapes();
  const [hover, setHover] = useState<(Delivery & { x: number; y: number }) | null>(null);
  const [phover, setPhover] = useState<ParcelHover | null>(null);
  // Kliknięta paczka „przypina" panel odbiorcy. Trzymamy klucz odbiorcy (nie
  // referencję), więc panel przeżywa odświeżenie danych UCS i sam się chowa,
  // gdy ostatnia paczka odbiorcy zniknie z mapy (dostarczona / zdjęta z kolejki).
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const group = useMemo(
    () => (selectedKey ? parcels.filter((p) => recipientKey(p) === selectedKey) : []),
    [selectedKey, parcels],
  );
  const head = group[0];

  // Kadr dopasowany do huba + punktów dostaw (auto-zoom Polska/Europa).
  const proj = useMemo(() => fitProjection([hub, ...deliveries]), [hub, deliveries]);
  const countryPaths = useMemo(
    () => shapes.map((s) => shapeToPath(s, proj)).filter(Boolean),
    [shapes, proj],
  );

  const hubXY = proj.project(hub.lon, hub.lat);
  const mapped = deliveries.map((d) => ({ ...d, xy: proj.project(d.lon, d.lat) }));
  const total = deliveries.reduce((s, d) => s + d.count, 0);
  const shippedCount = parcels.filter((p) => p.status === 'shipped').length;
  const prepCount = parcels.filter((p) => p.status === 'preparing').length;
  const liveCount = parcels.filter((p) => p.liveStatus).length; // realnie śledzone przez UCS

  return (
    <div className="card" style={{ overflow: 'hidden', ...(wall ? { border: 0, borderRadius: 0, boxShadow: 'none', background: 'transparent' } : null) }}>
      {!wall && (
        <div className="card-head">
          <div>
            <h3>Mapa dostaw — śledzenie na żywo</h3>
            <div className="ch-sub">
              {total} przesyłek
              {parcels.length > 0 && <> · <span className="num">{shippedCount}</span> w drodze · <span className="num">{prepCount}</span> kompletowanie</>}
              {' '}· hub: {hub.label}
            </div>
          </div>
          <div className="flex">
            {packedToday != null && (
              <span className="pill pill-ok"><Icon name="package-check" size={12} /> <span className="num">{packedToday}</span>&nbsp;spakowano</span>
            )}
            {liveCount > 0
              ? <span className="pill pill-info"><Icon name="radio" size={12} /> <span className="num">{liveCount}</span>&nbsp;UCS na żywo</span>
              : <span className="pill pill-info"><Icon name="radio" size={12} /> tracking</span>}
          </div>
        </div>
      )}
      <div className="map-wrap" style={wall ? { border: 0, borderRadius: 0, background: 'transparent' } : undefined}>
        {/* Czyszczenie panelu na pointerdown (spójnie z wyborem paczki) — press na
            paczce robi stopPropagation, więc tu trafia tylko press w puste tło/miasto. */}
        <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} preserveAspectRatio="xMidYMid meet" style={{ maxHeight: wall ? 'none' : 460 }} onPointerDown={() => setSelectedKey(null)}>
          <defs>
            <filter id="pf-glow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="5" />
            </filter>
            <radialGradient id="pf-hubglow">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.5" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </radialGradient>
          </defs>

          <g className="map-grat">
            <line x1="0" y1="240" x2="1000" y2="240" /><line x1="0" y1="480" x2="1000" y2="480" />
            <line x1="333" y1="0" x2="333" y2="720" /><line x1="666" y1="0" x2="666" y2="720" />
          </g>
          {countryPaths.map((d, i) => <path key={`cn${i}`} className="map-poland" d={d} />)}

          {/* trasy kurierów */}
          {mapped.map((d, i) => (
            <path key={`r${i}`} className={`map-route ${d.live ? 'live' : ''}`} d={arcPath(hubXY, d.xy)} stroke={courierColor(d.courier)} />
          ))}

          {/* żywe śledzenie paczek — pozycja = aktualny stan na trasie */}
          {parcels.length > 0 && (
            <ParcelLayer parcels={parcels} hubXY={hubXY} proj={proj} onHover={setPhover} onSelect={(p) => setSelectedKey(recipientKey(p))} />
          )}

          {/* znaczniki miast docelowych */}
          {mapped.map((d, i) => {
            const [cx, cy] = d.xy;
            const r = 5 + Math.sqrt(d.count) * 2.3;
            const col = courierColor(d.courier);
            return (
              <g key={`c${i}`} className="map-city" onMouseEnter={() => setHover({ ...d, x: cx, y: cy })} onMouseLeave={() => setHover(null)}>
                {d.live && (
                  <circle cx={cx} cy={cy} r={r + 6} fill={col} opacity="0.18">
                    <animate attributeName="r" values={`${r};${r + 12};${r}`} dur="2.2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.25;0;0.25" dur="2.2s" repeatCount="indefinite" />
                  </circle>
                )}
                <circle className="ring" cx={cx} cy={cy} r={r + 3} stroke={col} />
                <circle className="core" cx={cx} cy={cy} r={r} fill={col} />
                <text className="map-label" x={cx + r + 7} y={cy - 2}>{d.city}<tspan className="sub" dx="6">{d.count}</tspan></text>
              </g>
            );
          })}

          {/* hub z radarem */}
          <g className="map-city hub">
            <circle cx={hubXY[0]} cy={hubXY[1]} r="46" fill="url(#pf-hubglow)" />
            <circle cx={hubXY[0]} cy={hubXY[1]} r="15" fill="var(--accent)" opacity="0.2">
              <animate attributeName="r" values="15;30;15" dur="3s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.3;0;0.3" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle className="core" cx={hubXY[0]} cy={hubXY[1]} r="9" fill="var(--accent)" />
            <text className="map-label" x={hubXY[0]} y={hubXY[1] + 28} textAnchor="middle" style={{ fontWeight: 700 }}>{hub.label} · hub</text>
          </g>
        </svg>

        {hover && !head && (
          <div className="map-tooltip" style={{ left: `${(hover.x / MAP_W) * 100}%`, top: `${(hover.y / MAP_H) * 100}%` }}>
            {hover.city} · {hover.count} przesyłek
            <div className="t-sub">{COURIERS[hover.courier].label}{hover.live ? ' · w drodze' : ''}</div>
          </div>
        )}

        {phover && !head && (
          <div className="map-tooltip parcel-tip" style={{ left: `${(phover.x / MAP_W) * 100}%`, top: `${(phover.y / MAP_H) * 100}%` }}>
            <div className="pt-head">
              <span className="dot" style={{ background: courierColor(phover.p.courier) }} />{phover.p.orderName}
              {phover.real && <span className="pt-live">UCS</span>}
            </div>
            <div className="t-sub">{PHASE_LABEL[phover.phase]} · {(phover.real && phover.p.liveCity) || phover.p.city}{phover.p.customer ? ` · ${phover.p.customer}` : ''}</div>
            {phover.real
              ? <div className="t-sub">{COURIERS[phover.p.courier].label}{phover.p.liveDesc ? ` · ${phover.p.liveDesc}` : ''}{agoText(phover.p.liveAt) ? ` · ${agoText(phover.p.liveAt)}` : ''}</div>
              : <div className="t-sub">{COURIERS[phover.p.courier].label} · {etaText(phover.p, phover.phase, phover.progress)}</div>}
            <div className="pt-trk mono">{phover.real ? phover.p.tracking : `${phover.p.tracking} · podgląd`}</div>
            <div className="pt-hint">Kliknij, by zobaczyć szczegóły odbiorcy</div>
          </div>
        )}

        {/* Przypięty panel odbiorcy — po kliknięciu paczki: do kogo, ile paczek,
            numery śledzenia i ich status. Chowa się sam, gdy grupa pusta. */}
        {head && (
          <div className="parcel-panel" onClick={(e) => e.stopPropagation()}>
            <div className="pp-head">
              <div className="pp-who">
                <Icon name="user" size={14} />
                <span className="pp-name">{head.customer || head.orderName}</span>
              </div>
              <button type="button" className="pp-close" onClick={() => setSelectedKey(null)} aria-label="Zamknij panel">
                <Icon name="x" size={15} />
              </button>
            </div>
            <div className="pp-sub">
              <Icon name="map-pin" size={12} />
              {(head.liveCity || head.city)}{head.zip ? `, ${head.zip}` : ''}{head.country ? ` · ${head.country}` : ''}
            </div>
            <div className="pp-count">
              <Icon name="package" size={13} />
              <span className="num">{group.length}</span> {plPackages(group.length)} do tego odbiorcy
            </div>
            <div className="pp-list">
              {group.map((p) => {
                const ds = displayStatus(p);
                return (
                  <div className="pp-row" key={p.id}>
                    <span className="pp-cdot" style={{ background: courierColor(p.courier) }} title={COURIERS[p.courier].label} />
                    <div className="pp-main">
                      <div className="pp-l1">
                        <span className="pp-order mono">{p.orderName}</span>
                        <span className="pp-courier">{COURIERS[p.courier].label}</span>
                      </div>
                      <div className="pp-trk mono">{p.tracking}{!p.trackingNumber && <span className="pp-prev"> · podgląd</span>}</div>
                      {ds.real && p.liveDesc && (
                        <div className="pp-evt">{p.liveDesc}{agoText(p.liveAt) ? ` · ${agoText(p.liveAt)}` : ''}</div>
                      )}
                    </div>
                    <span className={`pp-status ${PHASE_DOT[ds.phase]}`}>
                      <span className="sw dot" />{ds.label}{ds.real && <span className="pp-live">UCS</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="map-legend">
          {(['inpost', 'dhl', 'goodspeed', 'cod'] as CourierKey[]).map((k) => (
            <div className="lg" key={k}><span className="sw" style={{ background: courierColor(k) }} />{COURIERS[k].label}</div>
          ))}
          <div className="lg" style={{ marginTop: 2 }}><span className="sw" style={{ background: 'var(--accent)' }} />Hub</div>
        </div>

        {parcels.length > 0 && (
          <div className="map-states">
            <div className="lg"><span className="sw dot transit" />W drodze</div>
            <div className="lg"><span className="sw dot ofd" />W doręczeniu</div>
            <div className="lg"><span className="sw dot done" />Dostarczona</div>
            <div className="lg"><span className="sw dot prep" />Kompletowanie</div>
          </div>
        )}
      </div>
    </div>
  );
}
